/**
 * Scroll performance auditor + live typography playground.
 *
 * Adds a "PERF" button. Tabs: Scroll (audit) | Type (fonts).
 * Only loads when the page URL includes ?tt-perf=1 (see layout/theme.liquid).
 *
 * Typography overrides are session-only (sessionStorage). Four roles:
 * Display (hero), Title (sections), Body (copy/eyebrows), UI (buttons/nav).
 * Injects Google Fonts on demand and overrides the matching selectors.
 *
 * To remove entirely: delete this file and the loader script in layout/theme.liquid.
 */
(function () {
  if (window.__ttPerf) return;
  if (!/[?&]tt-perf=1(?:&|$)/.test(window.location.search || '')) return;

  var FRAMES_PER_RUN = 90;
  var SCROLL_STEP = 14;
  var SETTLE_MS = 240;
  var NOOP = '{--tt-noop:0}';

  /* ---------------------------------------------------------------- tests */

  var EFFECT_TESTS = [
    { id: 'backdrop', label: 'Backdrop blur (glass)', css: '*,*::before,*::after{backdrop-filter:none!important;-webkit-backdrop-filter:none!important}' },
    { id: 'filter', label: 'CSS filters (blur etc)', css: '*,*::before,*::after{filter:none!important}' },
    { id: 'mask', label: 'Masks + clip-paths', css: '*,*::before,*::after{mask:none!important;-webkit-mask:none!important;clip-path:none!important;-webkit-clip-path:none!important}' },
    { id: 'shadow', label: 'Box + text shadows', css: '*,*::before,*::after{box-shadow:none!important;text-shadow:none!important}' },
    { id: 'anim', label: 'Animations + transitions', css: '*,*::before,*::after{animation:none!important;transition:none!important}' },
    { id: 'img', label: 'Images (img/video)', css: 'img,video,canvas{visibility:hidden!important}' },
    { id: 'bgimg', label: 'Background images', css: 'body *,body *::before,body *::after{background-image:none!important}' },
    { id: 'radius', label: 'Border radius', css: '*,*::before,*::after{border-radius:0!important}' },
    { id: 'opacity', label: 'Partial opacity', css: 'body *,body *::before,body *::after{opacity:1!important}' },
    { id: 'cv', label: 'content-visibility', css: '*{content-visibility:visible!important}' },
    { id: 'willchange', label: 'will-change', css: '*,*::before,*::after{will-change:auto!important}' },
    { id: 'overflow', label: 'overflow:hidden', css: 'body *{overflow:visible!important}' },
    { id: 'hero', label: 'Hero section', css: '.tt-hero{display:none!important}' },
    { id: 'stickynav', label: 'Sticky nav (un-stick)', css: '.tt-nav-section{position:static!important}' },
    { id: 'stickyhdr', label: 'Sticky header (un-stick)', css: '.header-wrapper{position:static!important}' },
    { id: 'floats', label: 'Floating corner buttons', css: '.tt-top,.tt-help-float{display:none!important}' },
    { id: 'cards', label: 'Product tile cards', css: '.tt-tile-card{visibility:hidden!important}' },
    { id: 'everything', label: '(ceiling) all content', css: 'main,footer,.shopify-section{display:none!important}' }
  ];

  function controlFor(css) {
    return css.split('{')[0] + NOOP;
  }

  /* ---------------------------------------------------------- type fonts */

  var TYPE_STORAGE_KEY = 'tt-perf-type-v2';

  /* Curated modern set — Google + system. "Moon Dance" = MOON. */
  var FONT_CATALOG = [
    { name: '(theme default)', id: '', kind: 'default' },
    { name: 'System UI', id: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', kind: 'system' },

    'Montserrat', 'Roboto', 'Poppins', 'Manrope', 'Lexend', 'Lexend Deca',
    'Outfit', 'Inter', 'DM Sans', 'Figtree', 'Plus Jakarta Sans',
    'Space Grotesk', 'Sora', 'Work Sans', 'Source Sans 3', 'Nunito Sans',
    'Urbanist', 'Onest', 'Instrument Sans', 'Public Sans', 'Albert Sans',
    'Hanken Grotesk', 'Epilogue', 'Jost', 'Raleway', 'Rubik', 'Mulish',
    'Karla', 'Barlow', 'Archivo', 'Red Hat Display', 'Red Hat Text',
    'Bricolage Grotesque', 'Schibsted Grotesk', 'Syne', 'Unbounded',
    'IBM Plex Sans', 'Fira Sans', 'Cabin', 'Asap', 'Exo 2',
    'Moon Dance'
  ];

  var FONTS = (function () {
    var seen = {};
    var out = [];
    FONT_CATALOG.forEach(function (entry) {
      var item = typeof entry === 'string'
        ? { name: entry, id: "'" + entry.replace(/'/g, "\\'") + "', sans-serif", kind: 'google', google: entry }
        : entry;
      if (item.kind === 'google' && !item.google) item.google = item.name;
      if (item.name === 'Moon Dance') {
        item.id = "'Moon Dance', cursive";
      }
      if (seen[item.name]) return;
      seen[item.name] = true;
      out.push(item);
    });
    out.sort(function (a, b) {
      if (a.kind === 'default') return -1;
      if (b.kind === 'default') return 1;
      if (a.kind === 'system' && b.kind !== 'system') return -1;
      if (b.kind === 'system' && a.kind !== 'system') return 1;
      return a.name.localeCompare(b.name);
    });
    return out;
  })();

  var TYPE_ROLES = [
    {
      id: 'display',
      label: 'Display',
      hint: 'Hero title',
      fontKey: 'displayFont',
      weightKey: 'displayWeight',
      defaultWeight: 700,
      nameAttr: 'data-display-name',
      fontSlot: 'data-display-font',
      weightAttr: 'data-display-weight',
      weightLabel: 'data-display-w',
      selectors: '.tt-hero__title,.tt-hero__title em,.tt-hero__cycle-item,.tt-hero__title-line'
    },
    {
      id: 'title',
      label: 'Title',
      hint: 'Section / page / product titles',
      fontKey: 'titleFont',
      weightKey: 'titleWeight',
      defaultWeight: 700,
      nameAttr: 'data-title-name',
      fontSlot: 'data-title-font',
      weightAttr: 'data-title-weight',
      weightLabel: 'data-title-w',
      selectors: [
        'h1:not(.tt-hero__title),h2:not(.tt-hero__title),h3,h4,h5,h6',
        '.h0,.h1,.h2,.h3,.h4,.h5',
        '.tt-section__title,.tt-about__title,.tt-help__heading,.tt-help__btn-title',
        '.tt-tile__title,.tt-tile-card__title,.tt-pdp__title,.tt-pdp__acc-btn,.tt-pdp__price,.tt-pdp__calc-label,.tt-pdp__similar-title,.tt-pdp__suit-app,.tt-pdp__suit-room-name',
        '.tt-collection__title,.tt-faqs__title,.tt-page-content__title,.tt-footer__col-title,.tt-stat__num',
        '.tt-blog__title,.tt-contact__title'
      ].join(',')
    },
    {
      id: 'body',
      label: 'Body',
      hint: 'Paragraphs + eyebrows',
      fontKey: 'bodyFont',
      weightKey: 'bodyWeight',
      defaultWeight: 500,
      nameAttr: 'data-body-name',
      fontSlot: 'data-body-font',
      weightAttr: 'data-body-weight',
      weightLabel: 'data-body-w',
      selectors: [
        'body,.text-body,.tt-hero__text,.tt-about__text',
        '.tt-eyebrow,.tt-stat__label,.tt-help__btn-text,.tt-tile__cta,.tt-tile__count',
        '.tt-badge,.tt-tile-card__size,.tt-review__text,.tt-pdp__size,.tt-pdp__highlights li,.tt-pdp__acc-panel,.tt-pdp__specs dt,.tt-pdp__specs dd,.tt-pdp .rte,.tt-pdp__delivery'
      ].join(',')
    },
    {
      id: 'ui',
      label: 'UI',
      hint: 'Buttons + nav pills + CTAs',
      fontKey: 'uiFont',
      weightKey: 'uiWeight',
      defaultWeight: 600,
      nameAttr: 'data-ui-name',
      fontSlot: 'data-ui-font',
      weightAttr: 'data-ui-weight',
      weightLabel: 'data-ui-w',
      selectors: [
        '.tt-btn,.tt-nav__btn,.tt-nav__menu-toggle',
        '.tt-nav__link,.tt-nav__drawer-link,.tt-nav__drawer-link--btn,.tt-nav__drawer-accordion',
        '.tt-filter-toggle__label,.tt-shopby__pill,.tt-top,.tt-help-float',
        '.tt-pdp__chip,.tt-pdp__colour-tile-label,.tt-pdp__atc,.tt-pdp__sample-btn,.tt-pdp__calc-input,.tt-pdp__back-in-stock-btn'
      ].join(',')
    }
  ];

  var typeState = {
    displayFont: '',
    displayWeight: 700,
    titleFont: '',
    titleWeight: 700,
    bodyFont: '',
    bodyWeight: 500,
    uiFont: '',
    uiWeight: 600
  };

  try {
    var saved = sessionStorage.getItem(TYPE_STORAGE_KEY);
    if (saved) {
      var parsed = JSON.parse(saved);
      if (parsed && typeof parsed === 'object') {
        TYPE_ROLES.forEach(function (role) {
          if (typeof parsed[role.fontKey] === 'string') typeState[role.fontKey] = parsed[role.fontKey];
          if (parsed[role.weightKey] != null) {
            typeState[role.weightKey] = Number(parsed[role.weightKey]) || role.defaultWeight;
          }
        });
      }
    }
  } catch (e) {}

  var typeStyle = document.createElement('style');
  typeStyle.id = 'tt-perf-type';
  document.head.appendChild(typeStyle);

  var loadedFonts = {};

  function findFont(name) {
    for (var i = 0; i < FONTS.length; i++) {
      if (FONTS[i].name === name) return FONTS[i];
    }
    return FONTS[0];
  }

  function loadGoogleFont(family) {
    if (!family || loadedFonts[family]) return;
    loadedFonts[family] = true;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=' +
      encodeURIComponent(family).replace(/%20/g, '+') +
      ':ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,700&display=swap';
    document.head.appendChild(link);
  }

  function persistType() {
    try {
      sessionStorage.setItem(TYPE_STORAGE_KEY, JSON.stringify(typeState));
    } catch (e) {}
  }

  function roleFont(role) {
    return findFont(typeState[role.fontKey] || '(theme default)');
  }

  function applyTypeStyles(persist) {
    var css = '';
    var title = roleFont(TYPE_ROLES[1]);
    var body = roleFont(TYPE_ROLES[2]);

    TYPE_ROLES.forEach(function (role) {
      var font = roleFont(role);
      var weight = typeState[role.weightKey];
      if (font.kind === 'google') loadGoogleFont(font.google);
      if (font.kind === 'default') return;
      css += role.selectors + '{font-family:' + font.id + '!important;font-weight:' + weight + '!important;}';
    });

    /* Keep theme CSS variables in sync when title/body are overridden. */
    if (title.kind !== 'default') {
      css += ':root{--font-heading-family:' + title.id + '!important;--font-heading-weight:' + typeState.titleWeight + '!important;}';
    }
    if (body.kind !== 'default') {
      css += ':root{--font-body-family:' + body.id + '!important;--font-body-weight:' + typeState.bodyWeight + '!important;}';
    }

    typeStyle.textContent = css;
    if (persist !== false) persistType();
    updateTypePreview();
  }

  function updateTypePreview() {
    if (!els.typePreview) return;
    var display = roleFont(TYPE_ROLES[0]);
    var title = roleFont(TYPE_ROLES[1]);
    var body = roleFont(TYPE_ROLES[2]);
    var ui = roleFont(TYPE_ROLES[3]);
    function face(font, weight) {
      return 'font-family:' + (font.kind === 'default' ? 'inherit' : font.id) + ';font-weight:' + weight;
    }
    els.typePreview.innerHTML =
      '<div class="prev-kicker">Display</div>' +
      '<div class="prev-display" style="' + face(display, typeState.displayWeight) + '">Chosen with care</div>' +
      '<div class="prev-kicker">Title</div>' +
      '<div class="prev-title" style="' + face(title, typeState.titleWeight) + '">Popular categories</div>' +
      '<div class="prev-kicker">Body</div>' +
      '<div class="prev-eyebrow" style="' + face(body, typeState.bodyWeight) + '">Premium porcelain tiles</div>' +
      '<div class="prev-body" style="' + face(body, typeState.bodyWeight) + '">Tiles curated with care for bathrooms, kitchens and outdoor spaces.</div>' +
      '<div class="prev-kicker">UI</div>' +
      '<div class="prev-ui" style="' + face(ui, typeState.uiWeight) + '">Shop tiles · All tiles</div>';
  }

  /* ------------------------------------------------------------ machinery */

  var overrideStyle = document.createElement('style');
  overrideStyle.id = 'tt-perf-overrides';
  document.head.appendChild(overrideStyle);

  var manual = {};
  var soloCss = null;

  function applyStyles() {
    if (soloCss !== null) {
      overrideStyle.textContent = soloCss;
      return;
    }
    var css = '';
    EFFECT_TESTS.forEach(function (test) {
      if (manual[test.id]) css += test.css + '\n';
    });
    overrideStyle.textContent = css;
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  var longTaskMs = 0;
  try {
    new PerformanceObserver(function (list) {
      list.getEntries().forEach(function (entry) { longTaskMs += entry.duration; });
    }).observe({ entryTypes: ['longtask'] });
  } catch (e) {}

  function summarise(deltas) {
    if (!deltas.length) return { fps: 0, avg: 0, worst: 0, slowPct: 0, blocking: 0 };
    var sorted = deltas.slice().sort(function (a, b) { return a - b; });
    var total = 0;
    var slow = 0;
    for (var i = 0; i < deltas.length; i++) {
      total += deltas[i];
      if (deltas[i] > 20) slow++;
    }
    var avg = total / deltas.length;
    return {
      fps: 1000 / avg,
      avg: avg,
      median: sorted[Math.floor(sorted.length / 2)],
      worst: sorted[sorted.length - 1],
      slowPct: (slow / deltas.length) * 100,
      blocking: longTaskMs
    };
  }

  function measure(scrolling) {
    return new Promise(function (resolve) {
      var deltas = [];
      var previous = 0;
      var frames = 0;
      var direction = 1;
      longTaskMs = 0;

      if (scrolling !== false) window.scrollTo(0, 0);

      requestAnimationFrame(function step(now) {
        if (previous) deltas.push(now - previous);
        previous = now;

        if (scrolling !== false) {
          var maxScroll = document.documentElement.scrollHeight - window.innerHeight;
          if (window.scrollY >= maxScroll - 2) direction = -1;
          if (window.scrollY <= 2 && frames > 4) direction = 1;
          window.scrollBy(0, SCROLL_STEP * direction);
        }

        frames++;
        if (frames < FRAMES_PER_RUN) {
          requestAnimationFrame(step);
        } else {
          resolve(summarise(deltas.slice(4)));
        }
      });
    });
  }

  /* ------------------------------------------------------------ DOM audit */

  function scanDom() {
    var counts = {
      'backdrop-filter': 0, 'filter': 0, 'mask / clip-path': 0, 'box-shadow': 0,
      'position: fixed': 0, 'position: sticky': 0, 'will-change': 0,
      'content-visibility: auto': 0, 'opacity < 1': 0, 'transform': 0
    };
    var nodes = document.body.getElementsByTagName('*');
    var total = nodes.length;

    for (var i = 0; i < total; i++) {
      if (nodes[i].closest && nodes[i].closest('#tt-perf-host')) continue;
      var cs = window.getComputedStyle(nodes[i]);
      if (cs.backdropFilter && cs.backdropFilter !== 'none') counts['backdrop-filter']++;
      if (cs.filter && cs.filter !== 'none') counts['filter']++;
      if ((cs.maskImage && cs.maskImage !== 'none') || (cs.clipPath && cs.clipPath !== 'none')) counts['mask / clip-path']++;
      if (cs.boxShadow && cs.boxShadow !== 'none') counts['box-shadow']++;
      if (cs.position === 'fixed') counts['position: fixed']++;
      if (cs.position === 'sticky') counts['position: sticky']++;
      if (cs.willChange && cs.willChange !== 'auto') counts['will-change']++;
      if (cs.contentVisibility === 'auto') counts['content-visibility: auto']++;
      if (cs.opacity && parseFloat(cs.opacity) < 1) counts['opacity < 1']++;
      if (cs.transform && cs.transform !== 'none') counts['transform']++;
    }

    var running = [];
    try {
      document.getAnimations().forEach(function (animation) {
        if (animation.playState !== 'running') return;
        var target = animation.effect && animation.effect.target;
        if (!target || !target.tagName) return;
        var first = target.className && typeof target.className === 'string'
          ? '.' + target.className.trim().split(/\s+/)[0]
          : '';
        running.push(target.tagName.toLowerCase() + first);
      });
    } catch (e) {}

    var tally = {};
    running.forEach(function (name) { tally[name] = (tally[name] || 0) + 1; });

    return { counts: counts, total: total, animations: tally, animationCount: running.length };
  }

  /* ----------------------------------------------------------------- view */

  var state = { report: '', busy: false };
  var els = {};
  var liveOn = true;
  var activeTab = 'scroll';

  function setTab(tab) {
    activeTab = tab;
    if (!els.tabScroll) return;
    els.tabScroll.classList.toggle('on', tab === 'scroll');
    els.tabType.classList.toggle('on', tab === 'type');
    els.paneScroll.style.display = tab === 'scroll' ? '' : 'none';
    els.paneType.style.display = tab === 'type' ? '' : 'none';
    els.hdTitle.textContent = tab === 'type' ? 'TYPOGRAPHY' : 'SCROLL AUDIT';
    els.panel.classList.toggle('wide', tab === 'type');
  }

  /* Custom picker — native <select> can't preview fonts on option hover. */
  var openFontPicker = null;

  function fontLabel(font) {
    if (font.kind === 'system') return font.name + ' · sys';
    if (font.name === 'Moon Dance') return 'Moon Dance · MOON';
    return font.name;
  }

  function buildFontPicker(selectedName, role) {
    var wrap = document.createElement('div');
    wrap.className = 'font-picker';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'font-picker__btn';

    var menu = document.createElement('div');
    menu.className = 'font-picker__menu';
    menu.hidden = true;

    var committed = selectedName || '(theme default)';
    var previewName = null;
    var hoverRaf = 0;
    var api;

    function setButtonLabel(name) {
      btn.textContent = fontLabel(findFont(name || '(theme default)'));
    }

    function paintRole(name, persist) {
      var value = name === '(theme default)' ? '' : name;
      var meta = null;
      for (var i = 0; i < TYPE_ROLES.length; i++) {
        if (TYPE_ROLES[i].id === role) meta = TYPE_ROLES[i];
      }
      if (!meta) return;
      typeState[meta.fontKey] = value;
      if (els.roleNames && els.roleNames[role]) {
        els.roleNames[role].textContent = value || 'theme';
      }
      applyTypeStyles(persist);
    }

    function preview(name) {
      previewName = name;
      if (hoverRaf) cancelAnimationFrame(hoverRaf);
      hoverRaf = requestAnimationFrame(function () {
        hoverRaf = 0;
        var font = findFont(name);
        if (font.kind === 'google') loadGoogleFont(font.google);
        paintRole(name, false);
        Array.prototype.forEach.call(menu.children, function (child) {
          child.classList.toggle('is-active', child.getAttribute('data-font') === name);
        });
      });
    }

    function revertPreview() {
      if (previewName === null) return;
      previewName = null;
      if (hoverRaf) {
        cancelAnimationFrame(hoverRaf);
        hoverRaf = 0;
      }
      paintRole(committed, false);
      Array.prototype.forEach.call(menu.children, function (child) {
        child.classList.toggle('is-active', false);
        child.classList.toggle('is-selected', child.getAttribute('data-font') === committed);
      });
    }

    function commit(name) {
      committed = name;
      previewName = null;
      setButtonLabel(committed);
      paintRole(committed, true);
      closeMenu();
    }

    function positionMenu() {
      var rect = btn.getBoundingClientRect();
      var maxH = Math.min(280, Math.max(140, window.innerHeight - rect.bottom - 12));
      menu.style.left = Math.round(rect.left) + 'px';
      menu.style.width = Math.round(rect.width) + 'px';
      menu.style.maxHeight = maxH + 'px';
      if (rect.bottom + maxH > window.innerHeight - 8 && rect.top > maxH + 8) {
        menu.style.top = 'auto';
        menu.style.bottom = Math.round(window.innerHeight - rect.top + 4) + 'px';
      } else {
        menu.style.bottom = 'auto';
        menu.style.top = Math.round(rect.bottom + 4) + 'px';
      }
    }

    function openMenu() {
      if (openFontPicker && openFontPicker !== api) openFontPicker.close();
      Array.prototype.forEach.call(menu.children, function (child) {
        var isSel = child.getAttribute('data-font') === committed;
        child.classList.toggle('is-selected', isSel);
        child.classList.toggle('is-active', false);
      });
      menu.hidden = false;
      positionMenu();
      openFontPicker = api;
      var selected = null;
      Array.prototype.forEach.call(menu.children, function (child) {
        if (child.getAttribute('data-font') === committed) selected = child;
      });
      if (selected) selected.scrollIntoView({ block: 'nearest' });
    }

    function closeMenu() {
      if (menu.hidden) return;
      menu.hidden = true;
      revertPreview();
      if (openFontPicker === api) openFontPicker = null;
    }

    FONTS.forEach(function (font) {
      var opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'font-picker__opt';
      opt.setAttribute('data-font', font.name);
      opt.textContent = fontLabel(font);
      if (font.kind !== 'default') {
        opt.style.fontFamily = font.id;
      }
      opt.addEventListener('mouseenter', function () {
        preview(font.name);
      });
      opt.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        commit(font.name);
      });
      menu.appendChild(opt);
    });

    menu.addEventListener('mouseleave', function () {
      revertPreview();
    });

    btn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      if (menu.hidden) openMenu();
      else closeMenu();
    });

    setButtonLabel(committed);
    wrap.appendChild(btn);
    /* Menu lives on host so fixed position isn't clipped by panel overflow. */
    document.getElementById('tt-perf-host').appendChild(menu);

    api = {
      el: wrap,
      get value() { return committed; },
      set value(name) {
        committed = name || '(theme default)';
        previewName = null;
        setButtonLabel(committed);
      },
      close: closeMenu,
      role: role
    };
    return api;
  }

  function build() {
    var old = document.getElementById('tt-perf-host');
    if (old && old.parentNode) old.parentNode.removeChild(old);

    var host = document.createElement('div');
    host.id = 'tt-perf-host';
    host.setAttribute('data-tt-perf-root', '1');
    document.body.appendChild(host);

    var style = document.createElement('style');
    style.textContent = [
      '#tt-perf-host{all:initial;position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483647}',
      '#tt-perf-host *,#tt-perf-host *::before,#tt-perf-host *::after{box-sizing:border-box}',
      '#tt-perf-host .tt-perf-ui{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;color:#e8edf3;pointer-events:auto}',
      '#tt-perf-host .fab{position:absolute;left:16px;bottom:72px;background:#1f6feb;color:#fff;border:0;border-radius:22px;padding:12px 18px;font-weight:700;cursor:pointer;letter-spacing:.06em;font-size:12px;line-height:1.1;min-height:40px;box-shadow:0 4px 18px rgba(0,0,0,.35)}',
      '#tt-perf-host .panel{position:absolute;left:16px;bottom:72px;width:340px;max-height:82vh;overflow:auto;background:#14171c;border:1px solid #2c333d;border-radius:10px;padding:12px;display:none;box-shadow:0 8px 28px rgba(0,0,0,.45)}',
      '#tt-perf-host .panel.wide{width:440px}',
      '#tt-perf-host .panel.open{display:block}',
      '#tt-perf-host .hd{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}',
      '#tt-perf-host .hd b{font-size:12px;letter-spacing:.06em}',
      '#tt-perf-host .tabs{display:flex;gap:4px;margin-bottom:10px}',
      '#tt-perf-host .tabs button{flex:1;padding:7px 8px;font-weight:700;letter-spacing:.04em}',
      '#tt-perf-host .tabs button.on{background:#1f6feb;border-color:#1f6feb;color:#fff}',
      '#tt-perf-host button{background:#222933;border:1px solid #39424f;border-radius:6px;padding:6px 9px;cursor:pointer;color:#e8edf3;font:inherit}',
      '#tt-perf-host button:hover{background:#2c3542}',
      '#tt-perf-host button.go{background:#1f6feb;border-color:#1f6feb;color:#fff;width:100%;padding:9px;font-weight:700;margin-bottom:6px}',
      '#tt-perf-host button:disabled{opacity:.5;cursor:default}',
      '#tt-perf-host button.alt{width:100%;margin-bottom:6px}',
      '#tt-perf-host .live{background:#0b0e12;border:1px solid #2c333d;border-radius:6px;padding:8px;text-align:center;margin-bottom:8px;font-size:13px;font-weight:700}',
      '#tt-perf-host .out{background:#0b0e12;border:1px solid #2c333d;border-radius:6px;padding:8px;margin-bottom:8px;white-space:pre;overflow-x:auto;line-height:1.5;display:none;font-size:10px}',
      '#tt-perf-host .sec{margin:11px 0 5px;color:#8b97a6;letter-spacing:.09em;font-size:9.5px;text-transform:uppercase}',
      '#tt-perf-host label.chk{display:flex;gap:7px;align-items:center;padding:2px 0;cursor:pointer;line-height:1.35}',
      '#tt-perf-host input{margin:0}',
      '#tt-perf-host .field{margin-bottom:12px}',
      '#tt-perf-host .field .lbl{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:5px;color:#8b97a6;font-size:10px;letter-spacing:.04em;text-transform:uppercase}',
      '#tt-perf-host .field .lbl span{color:#e8edf3;font-size:11px;letter-spacing:0;text-transform:none}',
      '#tt-perf-host .field .sub{color:#6b7582;font-size:10px;margin:-2px 0 6px;letter-spacing:0;text-transform:none}',
      '#tt-perf-host .font-picker{width:100%}',
      '#tt-perf-host .font-picker__btn{width:100%;text-align:left;background:#0b0e12;border:1px solid #2c333d;border-radius:6px;color:#e8edf3;padding:7px 8px;font-size:11px;cursor:pointer}',
      '#tt-perf-host .font-picker__btn:hover{background:#161b22;border-color:#39424f}',
      '#tt-perf-host .font-picker__menu{position:fixed;z-index:2147483647;pointer-events:auto;overflow:auto;background:#0b0e12;border:1px solid #2c333d;border-radius:6px;box-shadow:0 10px 28px rgba(0,0,0,.45);padding:4px;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;color:#e8edf3}',
      '#tt-perf-host .font-picker__opt{display:block;width:100%;text-align:left;background:transparent;border:0;border-radius:4px;color:#e8edf3;padding:7px 8px;font-size:12px;cursor:pointer;line-height:1.25}',
      '#tt-perf-host .font-picker__opt:hover,#tt-perf-host .font-picker__opt.is-active{background:#1f6feb;color:#fff}',
      '#tt-perf-host .font-picker__opt.is-selected{outline:1px solid rgba(255,255,255,.28)}',
      '#tt-perf-host input[type=range]{width:100%;accent-color:#1f6feb}',
      '#tt-perf-host .preview{background:#0b0e12;border:1px solid #2c333d;border-radius:6px;padding:12px;margin:4px 0 12px}',
      '#tt-perf-host .preview .prev-kicker{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:#6b7582;margin:8px 0 3px}',
      '#tt-perf-host .preview .prev-kicker:first-child{margin-top:0}',
      '#tt-perf-host .preview .prev-display{font-size:22px;line-height:1.1;color:#fff}',
      '#tt-perf-host .preview .prev-title{font-size:16px;line-height:1.2;color:#fff}',
      '#tt-perf-host .preview .prev-eyebrow{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#9aa7b5;margin-bottom:4px}',
      '#tt-perf-host .preview .prev-body{font-size:12px;line-height:1.45;color:#c5ced8}',
      '#tt-perf-host .preview .prev-ui{font-size:12px;line-height:1.3;color:#e8edf3}',
      '#tt-perf-host .hint{color:#8b97a6;font-size:10px;line-height:1.4;margin:0 0 10px}'
    ].join('');
    document.head.appendChild(style);

    var typeFieldsHtml = TYPE_ROLES.map(function (role) {
      return [
        '<div class="field">',
        '<div class="lbl">' + role.label + ' font <span ' + role.nameAttr + '></span></div>',
        '<div class="sub">' + role.hint + '</div>',
        '<div ' + role.fontSlot + '></div>',
        '</div>',
        '<div class="field">',
        '<div class="lbl">' + role.label + ' weight <span ' + role.weightLabel + '>' + role.defaultWeight + '</span></div>',
        '<input type="range" min="100" max="900" step="100" value="' + role.defaultWeight + '" ' + role.weightAttr + '>',
        '</div>'
      ].join('');
    }).join('');

    host.innerHTML = [
      '<button type="button" class="fab tt-perf-ui" data-a="open">PERF</button>',
      '<div class="panel tt-perf-ui" data-panel>',
      '<div class="hd"><b data-hd-title>SCROLL AUDIT</b><button type="button" data-a="close">\u2715</button></div>',
      '<div class="tabs">',
      '<button type="button" class="on" data-a="tab-scroll">Scroll</button>',
      '<button type="button" data-a="tab-type">Type</button>',
      '</div>',
      '<div data-pane-scroll>',
      '<div class="live" data-live>\u2014</div>',
      '<button type="button" class="go" data-a="audit">RUN FULL AUDIT</button>',
      '<button type="button" class="alt" data-a="sections">AUDIT SECTIONS ONE BY ONE</button>',
      '<button type="button" class="alt" data-a="scan">SCAN PAGE (no scrolling)</button>',
      '<div class="out" data-out></div>',
      '<button type="button" class="alt" data-a="copy">COPY REPORT</button>',
      '<div class="sec">Manual overrides</div>',
      '<div data-toggles></div>',
      '<button type="button" class="alt" data-a="reset" style="margin-top:8px">CLEAR OVERRIDES</button>',
      '</div>',
      '<div data-pane-type style="display:none">',
      '<p class="hint">4 roles · hover fonts to preview live · session only. Copy when you find a keeper.</p>',
      '<div class="preview" data-type-preview></div>',
      typeFieldsHtml,
      '<button type="button" class="alt" data-a="type-reset">RESET TO THEME FONTS</button>',
      '<button type="button" class="alt" data-a="type-copy">COPY CURRENT SET</button>',
      '</div>',
      '</div>'
    ].join('');

    var root = host;
    els.panel = root.querySelector('[data-panel]');
    els.fab = root.querySelector('.fab');
    els.live = root.querySelector('[data-live]');
    els.out = root.querySelector('[data-out]');
    els.hdTitle = root.querySelector('[data-hd-title]');
    els.tabScroll = root.querySelector('[data-a="tab-scroll"]');
    els.tabType = root.querySelector('[data-a="tab-type"]');
    els.paneScroll = root.querySelector('[data-pane-scroll]');
    els.paneType = root.querySelector('[data-pane-type]');
    els.typePreview = root.querySelector('[data-type-preview]');
    els.roleNames = {};
    els.rolePickers = {};
    els.roleWeights = {};
    els.roleWeightLabels = {};

    var toggles = root.querySelector('[data-toggles]');
    EFFECT_TESTS.forEach(function (test) {
      var label = document.createElement('label');
      label.className = 'chk';
      var box = document.createElement('input');
      box.type = 'checkbox';
      box.addEventListener('change', function () {
        manual[test.id] = box.checked;
        soloCss = null;
        applyStyles();
      });
      test.box = box;
      label.appendChild(box);
      label.appendChild(document.createTextNode(' ' + test.label));
      toggles.appendChild(label);
    });

    TYPE_ROLES.forEach(function (role) {
      var picker = buildFontPicker(typeState[role.fontKey] || '(theme default)', role.id);
      root.querySelector('[' + role.fontSlot + ']').appendChild(picker.el);
      els.rolePickers[role.id] = picker;
      els.roleNames[role.id] = root.querySelector('[' + role.nameAttr + ']');
      els.roleNames[role.id].textContent = typeState[role.fontKey] || 'theme';
      els.roleWeights[role.id] = root.querySelector('[' + role.weightAttr + ']');
      els.roleWeightLabels[role.id] = root.querySelector('[' + role.weightLabel + ']');
      els.roleWeights[role.id].value = String(typeState[role.weightKey]);
      els.roleWeightLabels[role.id].textContent = String(typeState[role.weightKey]);
      els.roleWeights[role.id].addEventListener('input', function () {
        typeState[role.weightKey] = Number(els.roleWeights[role.id].value) || role.defaultWeight;
        els.roleWeightLabels[role.id].textContent = String(typeState[role.weightKey]);
        applyTypeStyles();
      });
    });

    document.addEventListener('mousedown', function (event) {
      if (!openFontPicker) return;
      var t = event.target;
      var insidePicker = false;
      TYPE_ROLES.forEach(function (role) {
        if (els.rolePickers[role.id] && els.rolePickers[role.id].el.contains(t)) insidePicker = true;
      });
      if (insidePicker) return;
      if (t.closest && t.closest('.font-picker__menu')) return;
      openFontPicker.close();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && openFontPicker) openFontPicker.close();
    });
    window.addEventListener('resize', function () {
      if (openFontPicker) openFontPicker.close();
    });

    root.addEventListener('click', function (event) {
      var action = event.target.getAttribute && event.target.getAttribute('data-a');
      if (!action) return;
      if (action === 'open') { els.panel.classList.add('open'); els.fab.style.display = 'none'; }
      else if (action === 'close') { els.panel.classList.remove('open'); els.fab.style.display = ''; }
      else if (action === 'tab-scroll') setTab('scroll');
      else if (action === 'tab-type') setTab('type');
      else if (action === 'audit') runList(EFFECT_TESTS, event.target, 'RUN FULL AUDIT');
      else if (action === 'sections') runSectionAudit(event.target);
      else if (action === 'scan') showScan();
      else if (action === 'copy') copyReport();
      else if (action === 'reset') {
        manual = {};
        soloCss = null;
        EFFECT_TESTS.forEach(function (t) { t.box.checked = false; });
        applyStyles();
      } else if (action === 'type-reset') {
        TYPE_ROLES.forEach(function (role) {
          typeState[role.fontKey] = '';
          typeState[role.weightKey] = role.defaultWeight;
          els.rolePickers[role.id].value = '(theme default)';
          els.roleWeights[role.id].value = String(role.defaultWeight);
          els.roleWeightLabels[role.id].textContent = String(role.defaultWeight);
          els.roleNames[role.id].textContent = 'theme';
        });
        applyTypeStyles();
      } else if (action === 'type-copy') {
        var lines = TYPE_ROLES.map(function (role) {
          return role.label + ': ' + (typeState[role.fontKey] || 'theme default') + ' / ' + typeState[role.weightKey];
        });
        var text = lines.join('\n');
        try { navigator.clipboard.writeText(text); } catch (e) {}
        console.log(text);
      }
    });

    applyTypeStyles();
    startLiveMeter();
  }

  function startLiveMeter() {
    var last = 0;
    var frames = 0;
    var deltas = [];
    requestAnimationFrame(function loop(now) {
      if (last) {
        var delta = now - last;
        if (delta < 400) { deltas.push(delta); if (deltas.length > 45) deltas.shift(); }
      }
      last = now;
      frames++;
      if (liveOn && deltas.length && frames % 20 === 0) {
        var total = 0;
        for (var i = 0; i < deltas.length; i++) total += deltas[i];
        els.live.textContent = (1000 / (total / deltas.length)).toFixed(0) + ' fps';
      }
      requestAnimationFrame(loop);
    });
  }

  function show(text) {
    state.report = text;
    els.out.style.display = 'block';
    els.out.textContent = text;
  }

  function pad(text, width) {
    text = String(text);
    while (text.length < width) text += ' ';
    return text.slice(0, width);
  }

  function padLeft(text, width) {
    text = String(text);
    while (text.length < width) text = ' ' + text;
    return text;
  }

  /* -------------------------------------------------------------- the run */

  function runList(tests, button, title) {
    if (state.busy) return;
    state.busy = true;
    liveOn = false;
    button.disabled = true;

    var previousManual = manual;
    manual = {};

    var results = [];
    var refreshHz = 0;
    var noise = 0;
    var blocking = 0;
    var controlFps = [];

    function status(text) { button.textContent = text; }

    function runWith(css, scrolling) {
      soloCss = css;
      applyStyles();
      return wait(SETTLE_MS).then(function () { return measure(scrolling); });
    }

    function finish() {
      soloCss = null;
      manual = previousManual;
      applyStyles();
      state.busy = false;
      liveOn = true;
      button.disabled = false;
      status(title);

      var baseTotal = 0;
      controlFps.forEach(function (value) { baseTotal += value; });
      var baseline = baseTotal / (controlFps.length || 1);
      var threshold = Math.max(noise, 1.5);

      results.sort(function (a, b) { return b.gain - a.gain; });

      var text = title + '  \u2014  ' + new Date().toLocaleTimeString() + '\n';
      text += location.pathname + '\n\n';
      text += 'Display refresh ceiling : ' + refreshHz.toFixed(0) + ' fps\n';
      text += 'Baseline while scrolling: ' + baseline.toFixed(1) + ' fps\n';
      text += 'Main-thread blocking    : ' + blocking.toFixed(0) + ' ms\n';
      text += 'Measurement noise floor : \u00b1' + noise.toFixed(1) + ' fps\n\n';

      if (baseline > refreshHz - 6) {
        text += 'VERDICT: already at this display\'s ceiling. This machine is not\n';
        text += 'the one with the problem - run this on the slow machine.\n\n';
      } else if (blocking < 60) {
        text += 'VERDICT: main thread idle, so the cost is RASTER/GPU (painting),\n';
        text += 'not JavaScript. Look at the top rows below.\n\n';
      } else {
        text += 'VERDICT: main thread is busy, so JS / style / layout is involved.\n\n';
      }

      text += 'FPS GAINED BY DISABLING EACH (vs its own paired control)\n';
      text += '------------------------------------------------------\n';
      results.forEach(function (entry) {
        var significant = Math.abs(entry.gain) > threshold;
        text += pad(entry.label, 25) + ' ' +
          padLeft((entry.gain >= 0 ? '+' : '') + entry.gain.toFixed(1), 6) +
          (significant ? (entry.gain > 0 ? '  <<< REAL' : '  (slower)') : '   noise') + '\n';
      });

      if (!results.some(function (entry) { return entry.gain > threshold; })) {
        text += '\nNothing beat the noise floor: no single effect is the culprit here.\n';
      }

      show(text);
    }

    status('Calibrating\u2026');
    runWith('', false).then(function (idle) {
      refreshHz = 1000 / idle.median;

      status('Noise floor\u2026');
      var neutral = '*,*::before,*::after' + NOOP;
      return runWith(neutral, true).then(function (a) {
        return runWith(neutral, true).then(function (b) {
          noise = Math.abs(a.fps - b.fps);
          blocking = (a.blocking + b.blocking) / 2;
        });
      });
    }).then(function () {
      function next(index) {
        if (index >= tests.length) return finish();

        var test = tests[index];
        status(index + 1 + '/' + tests.length + ' ' + test.label.slice(0, 13));

        runWith(controlFor(test.css), true).then(function (control) {
          return runWith(test.css, true).then(function (stats) {
            controlFps.push(control.fps);
            results.push({ label: test.label, gain: stats.fps - control.fps });
            next(index + 1);
          });
        });
      }
      next(0);
    });
  }

  function runSectionAudit(button) {
    var tests = [];
    document.querySelectorAll('.shopify-section').forEach(function (section, index) {
      if (!section.id) section.id = 'tt-perf-section-' + index;
      var heading = section.querySelector('h1,h2,h3');
      var label = (heading && heading.textContent.trim().slice(0, 24)) || section.id.replace('shopify-section-', '');
      tests.push({ id: section.id, label: label, css: '[id="' + section.id + '"]{display:none!important}' });
    });
    if (!tests.length) { show('No .shopify-section elements found.'); return; }
    runList(tests, button, 'AUDIT SECTIONS ONE BY ONE');
  }

  function showScan() {
    var data = scanDom();
    var text = 'PAGE SCAN\n' + location.pathname + '\n\n';
    text += 'Elements in <body>: ' + data.total + '\n\n';
    text += 'EXPENSIVE PROPERTIES IN USE\n------------------------------------\n';
    Object.keys(data.counts).forEach(function (key) {
      text += pad(key, 28) + padLeft(data.counts[key], 5) + '\n';
    });
    text += '\nCSS ANIMATIONS RUNNING RIGHT NOW: ' + data.animationCount + '\n';
    text += '------------------------------------\n';
    var names = Object.keys(data.animations).sort(function (a, b) {
      return data.animations[b] - data.animations[a];
    });
    if (!names.length) text += '(none)\n';
    names.slice(0, 15).forEach(function (name) {
      text += pad(name, 30) + padLeft(data.animations[name], 5) + '\n';
    });
    show(text);
  }

  function copyReport() {
    if (!state.report) return;
    try { navigator.clipboard.writeText(state.report); } catch (e) {}
    console.log(state.report);
  }

  window.__ttPerf = { scan: scanDom, measure: measure, type: typeState };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
